/**
 * DatabaseTopologyView — DB cluster architecture (covers standalone, mirroring,
 * AlwaysOn AG, failover clusters, replicas and DR).
 *
 * Modelo:
 *   {
 *     database: {
 *       engine: 'sqlserver' | 'postgresql' | 'mysql' | 'mariadb' | 'oracle' |
 *               'mongodb' | 'other',
 *       mode:   'standalone' | 'mirror' | 'always_on_ag' | 'failover_cluster' |
 *               'replication_master_replica' | 'replication_multi_master' | 'sharded',
 *       listener: { name, ip, port },
 *       load_balancer: { name, type, vip },
 *       nodes:    [{ name, server_id, role ('primary'|'secondary'|'replica'|'dr'|'witness'),
 *                    sync_mode ('sync'|'async'|'none'), instance, port, location_id, notes }],
 *       instances:[{ name, port, db_count, version, notes }],
 *       storage:  { type ('san'|'local'|'cloud'|'nas'), description, capacity_tb },
 *       backup:   { full_freq, diff_freq, log_freq, retention, offsite_enabled, offsite_target, backup_server_id },
 *       dr:       { enabled, site_location_id, server_id, replication ('sync'|'async'), rpo, rto, notes },
 *       features: ['SQL AlwaysOn','TDE en reposo y tránsito','Monitorización 24/7',...],
 *     },
 *     notes: '...',
 *   }
 */
import React from 'react';
import { CircleStackIcon, ServerStackIcon, ArrowsRightLeftIcon, ArchiveBoxIcon, CloudArrowUpIcon, BoltIcon } from '@heroicons/react/24/outline';
import {
  useTopologyState, useTopologyCandidates, TopologyHeader,
  SectionBox, NodeCard, ListSection,
  TOPOLOGY_PALETTE, serverLabel, locationLabel,
} from './TopologyShared';

const DEFAULT = {
  database: {
    engine: 'sqlserver',
    mode: 'standalone',
    listener: { name: '', ip: '', port: '' },
    load_balancer: { name: '', type: '', vip: '' },
    nodes: [],
    instances: [],
    storage: { type: 'local', description: '', capacity_tb: '' },
    backup: { full_freq: '', diff_freq: '', log_freq: '', retention: '', offsite_enabled: false, offsite_target: '', backup_server_id: null },
    dr: { enabled: false, site_location_id: null, server_id: null, replication: 'async', rpo: '', rto: '', notes: '' },
    features: [],
  },
  notes: '',
};

const ENGINES = [
  { value: 'sqlserver',  label: 'SQL Server' },
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'mysql',      label: 'MySQL' },
  { value: 'mariadb',    label: 'MariaDB' },
  { value: 'oracle',     label: 'Oracle' },
  { value: 'mongodb',    label: 'MongoDB' },
  { value: 'other',      label: 'Otro' },
];

const MODES = [
  { value: 'standalone',                  label: 'Standalone (instancia única)' },
  { value: 'mirror',                      label: 'Mirroring / Database Mirroring' },
  { value: 'always_on_ag',                label: 'AlwaysOn Availability Group' },
  { value: 'failover_cluster',            label: 'Failover Cluster (WSFC / Pacemaker)' },
  { value: 'replication_master_replica',  label: 'Replicación Master / Replica' },
  { value: 'replication_multi_master',    label: 'Replicación Multi-Master' },
  { value: 'sharded',                     label: 'Sharded / Distribuido' },
];

const ROLES = [
  { value: 'primary',   label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'replica',   label: 'Replica' },
  { value: 'dr',        label: 'DR' },
  { value: 'witness',   label: 'Witness / Quorum' },
];

const SYNC = [
  { value: 'sync',  label: 'Síncrona' },
  { value: 'async', label: 'Asíncrona' },
  { value: 'none',  label: '—' },
];

const STORAGE_TYPES = [
  { value: 'local',  label: 'Local (DAS)' },
  { value: 'san',    label: 'SAN' },
  { value: 'nas',    label: 'NAS' },
  { value: 'cloud',  label: 'Cloud (S3, Azure Blob, etc.)' },
];

function nodeAccent(role) {
  if (role === 'primary') return TOPOLOGY_PALETTE.haAccent;
  if (role === 'dr')      return TOPOLOGY_PALETTE.dangerAccent;
  if (role === 'witness') return TOPOLOGY_PALETTE.secondaryAccent;
  return TOPOLOGY_PALETTE.primaryAccent;
}

export default function DatabaseTopologyView({ serviceId, companyId, serviceName }) {
  const state = useTopologyState(serviceId, companyId, DEFAULT);
  const { servers, locations } = useTopologyCandidates(serviceId, companyId);
  const { topology, setTopology, editMode, setEditMode,
          saving, error, loading, save, reload } = state;

  if (loading) return <div className="dv-tab-pane">Cargando topología BD…</div>;
  if (error)   return <div className="dv-tab-pane" style={{ color: '#f87171' }}>{error}</div>;

  const db  = topology.database || DEFAULT.database;
  const set = (k, v) => setTopology(t => ({ ...t, database: { ...t.database, [k]: v } }));
  const setObj = (key, k, v) => setTopology(t => ({ ...t, database: { ...t.database, [key]: { ...(t.database?.[key] || {}), [k]: v } } }));

  const isHA = db.mode && db.mode !== 'standalone';
  const accent = isHA ? TOPOLOGY_PALETTE.haAccent : TOPOLOGY_PALETTE.primaryAccent;

  const primaryNodes   = (db.nodes || []).filter(n => n.role === 'primary');
  const secondaryNodes = (db.nodes || []).filter(n => n.role === 'secondary' || n.role === 'replica');
  const drNodes        = (db.nodes || []).filter(n => n.role === 'dr');
  const otherNodes     = (db.nodes || []).filter(n => n.role === 'witness');

  return (
    <div className="dv-tab-pane" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TopologyHeader
        title={serviceName || 'Base de datos'}
        subtitle="Arquitectura de base de datos"
        editMode={editMode}
        saving={saving}
        onEdit={() => setEditMode(true)}
        onCancel={() => { setEditMode(false); reload(); }}
        onSave={save}
        onReload={reload}
      />

      {/* ── Motor + modo ── */}
      <SectionBox title="Motor y modo de despliegue" accent={accent}>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Motor</label>
            {editMode ? (
              <select className="form-select form-select-sm" value={db.engine}
                      onChange={e => set('engine', e.target.value)}>
                {ENGINES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <div style={{ color: TOPOLOGY_PALETTE.textStrong }}>
                {ENGINES.find(o => o.value === db.engine)?.label || db.engine}
              </div>
            )}
          </div>
          <div className="col-md-8">
            <label className="form-label">Modo</label>
            {editMode ? (
              <select className="form-select form-select-sm" value={db.mode}
                      onChange={e => set('mode', e.target.value)}>
                {MODES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <div style={{ color: TOPOLOGY_PALETTE.textStrong }}>
                {MODES.find(o => o.value === db.mode)?.label || db.mode}
                {isHA && <span style={{ marginLeft: 8, fontSize: '0.7rem', background: 'rgba(245,158,11,.18)', color: '#fbbf24', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>HA</span>}
              </div>
            )}
          </div>
        </div>
      </SectionBox>

      {/* ── Diagrama ── */}
      {!editMode && (
        <SectionBox title="Diagrama lógico" accent={accent}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Load balancer / listener */}
            {(db.load_balancer?.name || db.listener?.name) && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {db.load_balancer?.name && (
                  <NodeCard
                    icon={ArrowsRightLeftIcon}
                    accent={TOPOLOGY_PALETTE.secondaryAccent}
                    title={`LB · ${db.load_balancer.name}`}
                    subtitle={db.load_balancer.type}
                    footer={db.load_balancer.vip ? <div>VIP: {db.load_balancer.vip}</div> : null}
                  />
                )}
                {db.listener?.name && (
                  <NodeCard
                    icon={BoltIcon}
                    accent={TOPOLOGY_PALETTE.primaryAccent}
                    title={`Listener · ${db.listener.name}`}
                    subtitle="Punto único de conexión"
                    footer={
                      <>
                        {db.listener.ip   && <div>IP: {db.listener.ip}</div>}
                        {db.listener.port && <div>Puerto: {db.listener.port}</div>}
                      </>
                    }
                  />
                )}
              </div>
            )}

            {/* Nodes */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[...primaryNodes, ...secondaryNodes, ...otherNodes].map((n, i) => (
                <NodeCard
                  key={i}
                  icon={CircleStackIcon}
                  accent={nodeAccent(n.role)}
                  title={n.name || (n.server_id ? serverLabel(n.server_id, servers) : `Nodo ${i+1}`)}
                  subtitle={ROLES.find(r => r.value === n.role)?.label || n.role}
                  badges={[
                    ...(n.sync_mode && n.sync_mode !== 'none' ? [{
                      label: n.sync_mode === 'sync' ? 'Sync' : 'Async',
                      bg: n.sync_mode === 'sync' ? 'rgba(16,185,129,.18)' : 'rgba(234,179,8,.18)',
                      fg: n.sync_mode === 'sync' ? '#6ee7b7' : '#fde68a',
                    }] : []),
                  ]}
                  footer={
                    <>
                      {n.instance     && <div>Instancia: {n.instance}</div>}
                      {n.port         && <div>Puerto: {n.port}</div>}
                      {n.location_id  && <div>Sede: {locationLabel(n.location_id, locations)}</div>}
                      {n.server_id && !n.name && <div>{serverLabel(n.server_id, servers)}</div>}
                    </>
                  }
                />
              ))}
              {(db.nodes || []).length === 0 && (
                <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>
                  Sin nodos definidos.
                </div>
              )}
            </div>

            {/* DR */}
            {(db.dr?.enabled || drNodes.length > 0) && (
              <div style={{
                marginTop: 4, padding: 10,
                background: 'rgba(239,68,68,.06)',
                border: '1px dashed rgba(239,68,68,.4)',
                borderRadius: 8,
              }}>
                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#fca5a5', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Disaster Recovery Site
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {drNodes.map((n, i) => (
                    <NodeCard key={i} icon={CircleStackIcon} accent={TOPOLOGY_PALETTE.dangerAccent}
                      title={n.name || (n.server_id ? serverLabel(n.server_id, servers) : `DR ${i+1}`)}
                      subtitle="DR"
                      badges={n.sync_mode === 'sync' ? [{ label: 'Sync' }] : [{ label: 'Async', bg: 'rgba(234,179,8,.18)', fg: '#fde68a' }]}
                      footer={n.location_id ? <div>Sede: {locationLabel(n.location_id, locations)}</div> : null}
                    />
                  ))}
                  {db.dr?.enabled && (
                    <NodeCard icon={CloudArrowUpIcon} accent={TOPOLOGY_PALETTE.dangerAccent}
                      title="Plan DR"
                      subtitle={`Replicación ${db.dr.replication || 'async'}`}
                      footer={
                        <>
                          {db.dr.rpo && <div>RPO: {db.dr.rpo}</div>}
                          {db.dr.rto && <div>RTO: {db.dr.rto}</div>}
                          {db.dr.site_location_id && <div>Sede: {locationLabel(db.dr.site_location_id, locations)}</div>}
                        </>
                      }
                    />
                  )}
                </div>
                {db.dr?.notes && (
                  <div style={{ marginTop: 6, fontSize: '0.78rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>{db.dr.notes}</div>
                )}
              </div>
            )}

            {/* Storage + backup */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {db.storage?.type && (
                <NodeCard icon={ServerStackIcon} accent={TOPOLOGY_PALETTE.secondaryAccent}
                  title={`Almacenamiento · ${STORAGE_TYPES.find(s => s.value === db.storage.type)?.label || db.storage.type}`}
                  subtitle={db.storage.description}
                  footer={db.storage.capacity_tb ? <div>{db.storage.capacity_tb} TB</div> : null}
                />
              )}
              {(db.backup?.full_freq || db.backup?.offsite_enabled) && (
                <NodeCard icon={ArchiveBoxIcon} accent={TOPOLOGY_PALETTE.successAccent}
                  title="Backup"
                  subtitle={[
                    db.backup.full_freq && `Full ${db.backup.full_freq}`,
                    db.backup.diff_freq && `Diff ${db.backup.diff_freq}`,
                    db.backup.log_freq  && `Logs ${db.backup.log_freq}`,
                  ].filter(Boolean).join(' · ')}
                  footer={
                    <>
                      {db.backup.retention && <div>Retención: {db.backup.retention}</div>}
                      {db.backup.offsite_enabled && <div>Off-site: {db.backup.offsite_target || 'sí'}</div>}
                      {db.backup.backup_server_id && <div>{serverLabel(db.backup.backup_server_id, servers)}</div>}
                    </>
                  }
                />
              )}
            </div>
          </div>
        </SectionBox>
      )}

      {/* ── Listener / LB ── */}
      <SectionBox title="Punto de conexión (Listener / Load Balancer)" accent={TOPOLOGY_PALETTE.primaryAccent}>
        {editMode ? (
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Listener · nombre</label>
              <input className="form-control form-control-sm" value={db.listener?.name || ''}
                     onChange={e => setObj('listener', 'name', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Listener · IP</label>
              <input className="form-control form-control-sm" value={db.listener?.ip || ''}
                     onChange={e => setObj('listener', 'ip', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Listener · puerto</label>
              <input type="number" className="form-control form-control-sm" value={db.listener?.port || ''}
                     onChange={e => setObj('listener', 'port', e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="col-md-4">
              <label className="form-label">LB · nombre</label>
              <input className="form-control form-control-sm" value={db.load_balancer?.name || ''}
                     onChange={e => setObj('load_balancer', 'name', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">LB · tipo</label>
              <input className="form-control form-control-sm" placeholder="HAProxy / F5 / NLB"
                     value={db.load_balancer?.type || ''}
                     onChange={e => setObj('load_balancer', 'type', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">LB · VIP</label>
              <input className="form-control form-control-sm" value={db.load_balancer?.vip || ''}
                     onChange={e => setObj('load_balancer', 'vip', e.target.value)} />
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textStrong }}>
            {db.listener?.name      ? <>Listener <strong>{db.listener.name}</strong> ({db.listener.ip}:{db.listener.port})</> : <em>Sin listener</em>}
            {db.load_balancer?.name && <> · LB <strong>{db.load_balancer.name}</strong> {db.load_balancer.vip && `(VIP ${db.load_balancer.vip})`}</>}
          </div>
        )}
      </SectionBox>

      <ListSection
        title="Nodos del cluster"
        accent={accent}
        items={db.nodes}
        onChange={(v) => set('nodes', v)}
        editMode={editMode}
        servers={servers}
        locations={locations}
        defaultRow={{ role: 'primary', sync_mode: 'sync' }}
        columns={[
          { key: 'name',        label: 'Nombre',     placeholder: 'SQL-Node-01' },
          { key: 'server_id',   label: 'Servidor',   type: 'server' },
          { key: 'role',        label: 'Rol',        type: 'select', options: ROLES },
          { key: 'sync_mode',   label: 'Sincronía',  type: 'select', options: SYNC },
          { key: 'instance',    label: 'Instancia',  placeholder: 'MSSQLSERVER' },
          { key: 'port',        label: 'Puerto',     type: 'number',  placeholder: '1433' },
          { key: 'location_id', label: 'Sede',       type: 'location' },
        ]}
        addLabel="Añadir nodo"
        emptyText="Sin nodos."
      />

      <ListSection
        title="Instancias / Bases de datos"
        accent={TOPOLOGY_PALETTE.secondaryAccent}
        items={db.instances}
        onChange={(v) => set('instances', v)}
        editMode={editMode}
        servers={servers}
        locations={locations}
        defaultRow={{}}
        columns={[
          { key: 'name',     label: 'Instancia/BD',  placeholder: 'ERP_PROD' },
          { key: 'port',     label: 'Puerto',        type: 'number' },
          { key: 'db_count', label: 'Nº BDs',        type: 'number' },
          { key: 'version',  label: 'Versión',       placeholder: 'SQL 2022 / PG 16' },
          { key: 'notes',    label: 'Notas' },
        ]}
        addLabel="Añadir instancia"
        emptyText="Sin instancias detalladas."
      />

      {/* ── Storage ── */}
      <SectionBox title="Almacenamiento" accent={TOPOLOGY_PALETTE.secondaryAccent}>
        {editMode ? (
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Tipo</label>
              <select className="form-select form-select-sm" value={db.storage?.type || 'local'}
                      onChange={e => setObj('storage', 'type', e.target.value)}>
                {STORAGE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Capacidad (TB)</label>
              <input type="number" step="0.1" className="form-control form-control-sm"
                     value={db.storage?.capacity_tb || ''}
                     onChange={e => setObj('storage', 'capacity_tb', e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Descripción</label>
              <input className="form-control form-control-sm" placeholder="SAN Fibre 16Gb / NVMe local…"
                     value={db.storage?.description || ''}
                     onChange={e => setObj('storage', 'description', e.target.value)} />
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textStrong }}>
            {STORAGE_TYPES.find(s => s.value === db.storage?.type)?.label || db.storage?.type || '—'}
            {db.storage?.capacity_tb && <> · {db.storage.capacity_tb} TB</>}
            {db.storage?.description && <> · {db.storage.description}</>}
          </div>
        )}
      </SectionBox>

      {/* ── Backup ── */}
      <SectionBox title="Política de backup" accent={TOPOLOGY_PALETTE.successAccent}>
        {editMode ? (
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Full</label>
              <input className="form-control form-control-sm" placeholder="Diario / 6h"
                     value={db.backup?.full_freq || ''}
                     onChange={e => setObj('backup', 'full_freq', e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Differential</label>
              <input className="form-control form-control-sm" placeholder="Cada 6h"
                     value={db.backup?.diff_freq || ''}
                     onChange={e => setObj('backup', 'diff_freq', e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Transaction Log</label>
              <input className="form-control form-control-sm" placeholder="Cada 15 min"
                     value={db.backup?.log_freq || ''}
                     onChange={e => setObj('backup', 'log_freq', e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Retención</label>
              <input className="form-control form-control-sm" placeholder="30 días / 1 año"
                     value={db.backup?.retention || ''}
                     onChange={e => setObj('backup', 'retention', e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6, marginTop: 24 }}>
                <input type="checkbox" className="form-check-input"
                       checked={!!db.backup?.offsite_enabled}
                       onChange={e => setObj('backup', 'offsite_enabled', e.target.checked)} />
                <span>Copia off-site / cloud</span>
              </label>
            </div>
            <div className="col-md-5">
              <label className="form-label">Destino off-site</label>
              <input className="form-control form-control-sm" placeholder="Azure Blob / S3 / Tape"
                     value={db.backup?.offsite_target || ''}
                     onChange={e => setObj('backup', 'offsite_target', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Servidor de backup</label>
              <select className="form-select form-select-sm" value={db.backup?.backup_server_id || ''}
                      onChange={e => setObj('backup', 'backup_server_id', e.target.value ? Number(e.target.value) : null)}>
                <option value="">—</option>
                {(servers || []).map(s => (
                  <option key={`${s.kind}-${s.id}`} value={s.id}>
                    {s.hostname || s.name}{s.ip ? ` · ${s.ip}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textStrong }}>
            {[db.backup?.full_freq && `Full ${db.backup.full_freq}`,
              db.backup?.diff_freq && `Diff ${db.backup.diff_freq}`,
              db.backup?.log_freq  && `Logs ${db.backup.log_freq}`,
              db.backup?.retention && `Retención ${db.backup.retention}`,
              db.backup?.offsite_enabled && `Off-site: ${db.backup.offsite_target || 'sí'}`]
              .filter(Boolean).join(' · ') || <em>Sin política de backup definida</em>}
          </div>
        )}
      </SectionBox>

      {/* ── DR ── */}
      <SectionBox title="Plan de Recuperación ante Desastres" accent={TOPOLOGY_PALETTE.dangerAccent}>
        {editMode ? (
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6, marginTop: 24 }}>
                <input type="checkbox" className="form-check-input"
                       checked={!!db.dr?.enabled}
                       onChange={e => setObj('dr', 'enabled', e.target.checked)} />
                <span>DR habilitado</span>
              </label>
            </div>
            <div className="col-md-3">
              <label className="form-label">Sede DR</label>
              <select className="form-select form-select-sm" value={db.dr?.site_location_id || ''}
                      onChange={e => setObj('dr', 'site_location_id', e.target.value ? Number(e.target.value) : null)}>
                <option value="">—</option>
                {(locations || []).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Servidor DR</label>
              <select className="form-select form-select-sm" value={db.dr?.server_id || ''}
                      onChange={e => setObj('dr', 'server_id', e.target.value ? Number(e.target.value) : null)}>
                <option value="">—</option>
                {(servers || []).map(s => <option key={`${s.kind}-${s.id}`} value={s.id}>{s.hostname || s.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Replicación</label>
              <select className="form-select form-select-sm" value={db.dr?.replication || 'async'}
                      onChange={e => setObj('dr', 'replication', e.target.value)}>
                <option value="sync">Síncrona</option>
                <option value="async">Asíncrona</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">RPO</label>
              <input className="form-control form-control-sm" placeholder="15 min / 1h"
                     value={db.dr?.rpo || ''} onChange={e => setObj('dr', 'rpo', e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">RTO</label>
              <input className="form-control form-control-sm" placeholder="2h / 4h"
                     value={db.dr?.rto || ''} onChange={e => setObj('dr', 'rto', e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Notas</label>
              <input className="form-control form-control-sm" value={db.dr?.notes || ''}
                     onChange={e => setObj('dr', 'notes', e.target.value)} />
            </div>
          </div>
        ) : (
          db.dr?.enabled ? (
            <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textStrong }}>
              Réplica {db.dr.replication}
              {db.dr.site_location_id && <> · sede {locationLabel(db.dr.site_location_id, locations)}</>}
              {db.dr.server_id && <> · servidor {serverLabel(db.dr.server_id, servers)}</>}
              {db.dr.rpo && <> · RPO {db.dr.rpo}</>}
              {db.dr.rto && <> · RTO {db.dr.rto}</>}
              {db.dr.notes && <div style={{ marginTop: 4, color: TOPOLOGY_PALETTE.textMuted }}>{db.dr.notes}</div>}
            </div>
          ) : (
            <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>
              Sin plan DR documentado.
            </div>
          )
        )}
      </SectionBox>

      {/* ── Características ── */}
      <SectionBox title="Características clave" accent={TOPOLOGY_PALETTE.successAccent}>
        {editMode ? (
          <textarea className="form-control" rows={4}
            placeholder="Una característica por línea (ej. SQL Server AlwaysOn, Réplicas síncronas, TDE en reposo, Monitorización 24/7, Plan DR validado…)"
            value={(db.features || []).join('\n')}
            onChange={e => set('features', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
          />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(db.features || []).length === 0 && (
              <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>Sin características.</div>
            )}
            {(db.features || []).map((f, i) => (
              <span key={i} style={{ background: 'rgba(16,185,129,.12)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,.35)', padding: '3px 8px', borderRadius: 12, fontSize: '0.78rem' }}>{f}</span>
            ))}
          </div>
        )}
      </SectionBox>
    </div>
  );
}
